'use client';

import { c as _c } from "react/compiler-runtime";
import { MapPin, NavArrowLeft, NavArrowRight } from '@byway/icons';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button as RACButton, Radio, RadioGroup, Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { Button } from '~/components/Button';
import { CenteredModal } from '~/components/CenteredModal';
import { Divider } from '~/components/Divider';
import { OverlayDrawer } from '~/components/Drawer';
import { Spinner } from '~/components/Spinner/Spinner';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { api } from '~/libs/api';
import styles from './ChangeOriginModal.module.css';
const byLondonFirst = (a: {
  locationName: string;
}, b: {
  locationName: string;
}) => {
  if (a.locationName === 'London') return -1;
  if (b.locationName === 'London') return 1;
  return a.locationName.localeCompare(b.locationName);
};
const POPULAR_ORIGINS = {
  UK: ['London', 'Glasgow', 'Edinburgh', 'Bristol'],
  EU: ['Madrid', 'Paris', 'Milan', 'Lyon']
};
type ChangeOriginModalProps = {
  isOpen: boolean;
  close: () => void;
  currentOrigin: string;
  tripId: string;
  onConfirm: (originName: string) => Promise<boolean>;
};
export function ChangeOriginModal(t0) {
  const $ = _c(89);
  const {
    isOpen,
    close,
    currentOrigin,
    tripId,
    onConfirm
  } = t0;
  const t = useTranslations("journey");
  const commonT = useTranslations("common");
  const formsT = useTranslations("forms");
  const [pendingOrigin, setPendingOrigin] = useState(currentOrigin);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeRegion, setActiveRegion] = useState(null);
  const isTripUpdating = useIsTripUpdating();
  const isDesktop = useIsDesktop();
  let t1;
  if ($[0] !== tripId) {
    t1 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isOpen) {
    t2 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: isOpen
    };
    $[2] = isOpen;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: t3,
    isLoading
  } = api.cms.origins.useQuery(t1, t2);
  const origins = t3 === undefined ? [] : t3;
  let t4;
  let t5;
  if ($[4] !== currentOrigin || $[5] !== isOpen) {
    t4 = () => {
      if (!isOpen) {
        return;
      }
      setPendingOrigin(currentOrigin);
      setActiveRegion(null);
    };
    t5 = [currentOrigin, isOpen];
    $[4] = currentOrigin;
    $[5] = isOpen;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  const ukOrigins = origins.filter(_temp).sort(byLondonFirst);
  const euOrigins = origins.filter(_temp2);
  const regionGroups = [{
    key: "UK" as const,
    label: t("origin_uk"),
    popularLabel: t("origin_popular_uk"),
    allLabel: t("origin_all_uk"),
    origins: ukOrigins
  }, {
    key: "EU" as const,
    label: t("origin_europe"),
    popularLabel: t("origin_popular_europe"),
    allLabel: t("origin_all_europe"),
    origins: euOrigins
  }].filter(_temp3);
  let t6;
  let t7;
  if ($[8] !== isDesktop || $[9] !== isOpen) {
    t6 = () => {
      if (isDesktop || !isOpen) {
        return;
      }
      const root = document.documentElement;
      const viewport = window.visualViewport;
      const update = () => {
        const height = viewport?.height ?? window.innerHeight;
        root.style.setProperty("--visual-viewport-height", `${height}px`);
      };
      update();
      viewport?.addEventListener("resize", update);
      viewport?.addEventListener("scroll", update);
      return () => {
        viewport?.removeEventListener("resize", update);
        viewport?.removeEventListener("scroll", update);
        root.style.removeProperty("--visual-viewport-height");
      };
    };
    t7 = [isOpen, isDesktop];
    $[8] = isDesktop;
    $[9] = isOpen;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== close || $[13] !== onConfirm || $[14] !== pendingOrigin) {
    t8 = async () => {
      setIsSubmitting(true);
      const success = await onConfirm(pendingOrigin);
      setIsSubmitting(false);
      if (success) {
        close();
      }
    };
    $[12] = close;
    $[13] = onConfirm;
    $[14] = pendingOrigin;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleConfirm = t8;
  let t9;
  if ($[16] !== currentOrigin) {
    t9 = () => {
      setActiveRegion(null);
      setPendingOrigin(currentOrigin);
    };
    $[16] = currentOrigin;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleBack = t9;
  let t10;
  if ($[18] !== currentOrigin) {
    t10 = () => setPendingOrigin(currentOrigin);
    $[18] = currentOrigin;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleCancel = t10;
  let t11;
  if ($[20] !== close || $[21] !== currentOrigin) {
    t11 = () => {
      setPendingOrigin(currentOrigin);
      setActiveRegion(null);
      close();
    };
    $[20] = close;
    $[21] = currentOrigin;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const handleDismiss = t11;
  const activeGroup = regionGroups.find(group_0 => group_0.key === activeRegion);
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className={styles.popoverLoading}><Spinner /></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const loadingView = t12;
  let t13;
  if ($[24] !== t) {
    t13 = t("no_origins");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <p className={styles.helperText}>{t13}</p>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const noOriginsView = t14;
  if (isDesktop) {
    const currentRegion = euOrigins.some(origin_1 => origin_1.locationName === currentOrigin) ? "EU" : "UK";
    const renderOriginRadio = _temp4;
    const dialogRegionView = regionGroups.length > 0 ? <Tabs className={styles.dialogTabsRoot} selectedKey={activeRegion ?? currentRegion} onSelectionChange={key => setActiveRegion(key as 'UK' | 'EU')}><TabList aria-label={t("origin_heading")} className={styles.dialogTabs}>{regionGroups.map(_temp5)}</TabList>{regionGroups.map(group_2 => {
        const popularNames = POPULAR_ORIGINS[group_2.key];
        const popular = popularNames.flatMap(name => group_2.origins.find(origin_3 => origin_3.locationName === name) ?? []);
        const rest = group_2.origins.filter(origin_4 => !popularNames.includes(origin_4.locationName));
        return <TabPanel key={group_2.key} id={group_2.key} className={styles.dialogPanel}><RadioGroup aria-label={group_2.label} value={pendingOrigin} onChange={setPendingOrigin} isDisabled={isSubmitting || isTripUpdating} className={styles.dialogList}>{popular.length > 0 && <p className={styles.dialogListHeading}>{group_2.popularLabel}</p>}{popular.map(renderOriginRadio)}{rest.length > 0 && <p className={styles.dialogListHeading}>{group_2.allLabel}</p>}{rest.map(renderOriginRadio)}</RadioGroup></TabPanel>;
      })}</Tabs> : noOriginsView;
    let t15;
    if ($[28] !== t) {
      t15 = t("booking_choose_origin");
      $[28] = t;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    const t16 = isLoading ? loadingView : dialogRegionView;
    let t17;
    if ($[30] !== t16) {
      t17 = <div className={styles.dialogBody}>{t16}</div>;
      $[30] = t16;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    let t18;
    if ($[32] !== formsT) {
      t18 = formsT("cancel");
      $[32] = formsT;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    let t19;
    if ($[34] !== handleDismiss || $[35] !== isSubmitting || $[36] !== t18) {
      t19 = <RACButton className={styles.dialogCancel} onPress={handleDismiss} isDisabled={isSubmitting}>{t18}</RACButton>;
      $[34] = handleDismiss;
      $[35] = isSubmitting;
      $[36] = t18;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    const t20 = pendingOrigin === currentOrigin || isTripUpdating;
    let t21;
    if ($[38] !== t) {
      t21 = t("origin_save_changes");
      $[38] = t;
      $[39] = t21;
    } else {
      t21 = $[39];
    }
    let t22;
    if ($[40] !== handleConfirm || $[41] !== isSubmitting || $[42] !== t20 || $[43] !== t21) {
      t22 = <Button variant="primary" isPillShape={true} onPress={handleConfirm} isDisabled={t20} isLoading={isSubmitting} dataTestId="change-origin-confirm">{t21}</Button>;
      $[40] = handleConfirm;
      $[41] = isSubmitting;
      $[42] = t20;
      $[43] = t21;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    let t23;
    if ($[45] !== t19 || $[46] !== t22) {
      t23 = <div className={styles.dialogFooter}>{t19}{t22}</div>;
      $[45] = t19;
      $[46] = t22;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    let t24;
    if ($[48] !== handleDismiss || $[49] !== isOpen || $[50] !== t15 || $[51] !== t17 || $[52] !== t23) {
      t24 = <CenteredModal isOpen={isOpen} close={handleDismiss} ariaLabel={t15} dataTestId="ChangeOriginDialog">{t17}{t23}</CenteredModal>;
      $[48] = handleDismiss;
      $[49] = isOpen;
      $[50] = t15;
      $[51] = t17;
      $[52] = t23;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    return t24;
  }
  let t15;
  if ($[54] !== currentOrigin || $[55] !== pendingOrigin) {
    t15 = pendingOrigin !== currentOrigin && pendingOrigin.trim().length > 0;
    $[54] = currentOrigin;
    $[55] = pendingOrigin;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  const showFooter = t15;
  const drawerRegionView = regionGroups.length > 0 ? <ul className={styles.drawerList} aria-label={t("origin_heading")}>{regionGroups.map(group_3 => <li key={group_3.key}><button type="button" className={styles.drawerRegionRow} onClick={() => setActiveRegion(group_3.key)}><span>{group_3.label}</span><NavArrowRight decorative={true} size="small" width={18} height={18} /></button></li>)}</ul> : noOriginsView;
  const drawerOriginsView = activeGroup ? <ul className={styles.drawerList} aria-label={activeGroup.label}>{activeGroup.origins.map(origin_5 => {
      const isSelected = origin_5.locationName === pendingOrigin;
      return <li key={origin_5.id}><button type="button" aria-current={isSelected ? "true" : undefined} className={styles.drawerOriginRow} data-selected={isSelected || undefined} disabled={isSubmitting || isTripUpdating} onClick={() => setPendingOrigin(origin_5.locationName)}><MapPin decorative={true} size="small" width={20} height={20} /><span>{origin_5.locationName}</span></button></li>;
    })}</ul> : drawerRegionView;
  let t16;
  if ($[57] !== activeGroup || $[58] !== commonT || $[59] !== handleBack) {
    t16 = activeGroup && <button type="button" className={styles.drawerBack} onClick={handleBack} aria-label={commonT("back")}><NavArrowLeft decorative={true} size="small" width={18} height={18} /></button>;
    $[57] = activeGroup;
    $[58] = commonT;
    $[59] = handleBack;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== activeGroup || $[62] !== t) {
    t17 = activeGroup ? activeGroup.label : t("origin_heading");
    $[61] = activeGroup;
    $[62] = t;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t17) {
    t18 = <span className={styles.drawerTitle}>{t17}</span>;
    $[64] = t17;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== t16 || $[67] !== t18) {
    t19 = <div className={styles.drawerHeader}>{t16}{t18}</div>;
    $[66] = t16;
    $[67] = t18;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  const drawerHeader = t19;
  let t20;
  if ($[69] !== t) {
    t20 = t("booking_choose_origin");
    $[69] = t;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Divider variant="horizontal" />;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  const t22 = isLoading ? loadingView : drawerOriginsView;
  let t23;
  if ($[72] !== formsT || $[73] !== handleCancel || $[74] !== handleConfirm || $[75] !== isSubmitting || $[76] !== isTripUpdating || $[77] !== showFooter || $[78] !== t) {
    t23 = showFooter && <div className={styles.drawerFooter}><div className={styles.drawerFooterCol}><Button variant="pill" size="fullWidth" onPress={handleCancel} isDisabled={isSubmitting}>{formsT("cancel")}</Button></div><div className={clsx(styles.drawerFooterCol, styles.drawerFooterConfirm)}><Button variant="primary" size="fullWidth" onPress={handleConfirm} isPillShape={true} isDisabled={isTripUpdating} isLoading={isSubmitting} dataTestId="change-origin-confirm">{t("confirm")}</Button></div></div>;
    $[72] = formsT;
    $[73] = handleCancel;
    $[74] = handleConfirm;
    $[75] = isSubmitting;
    $[76] = isTripUpdating;
    $[77] = showFooter;
    $[78] = t;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== t22 || $[81] !== t23) {
    t24 = <div className={styles.drawerRoot}>{t21}{t22}{t23}</div>;
    $[80] = t22;
    $[81] = t23;
    $[82] = t24;
  } else {
    t24 = $[82];
  }
  let t25;
  if ($[83] !== close || $[84] !== drawerHeader || $[85] !== isOpen || $[86] !== t20 || $[87] !== t24) {
    t25 = <OverlayDrawer isOpen={isOpen} close={close} ariaLabel={t20} headerContent={drawerHeader} dataTestId="ChangeOriginDrawer">{t24}</OverlayDrawer>;
    $[83] = close;
    $[84] = drawerHeader;
    $[85] = isOpen;
    $[86] = t20;
    $[87] = t24;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  return t25;
}
function _temp5(group_1) {
  return <Tab key={group_1.key} id={group_1.key} className={styles.dialogTab}>{group_1.label}</Tab>;
}
function _temp4(origin_2) {
  return <Radio key={origin_2.id} value={origin_2.locationName} className={styles.dialogOption}><MapPin decorative={true} size="small" width={20} height={20} /><span className={styles.dialogOptionLabel}>{origin_2.locationName}</span><span className={styles.dialogOptionRadio} aria-hidden="true" /></Radio>;
}
function _temp3(group) {
  return group.origins.length > 0;
}
function _temp2(origin_0) {
  return origin_0.country?.countryId !== "UK";
}
function _temp(origin) {
  return origin.country?.countryId === "UK";
}